import React, { useEffect, useState, useRef } from "react";
import Select from "react-select";
import {
  getCandidateList,
  getCities,
  getJobTitles,
} from "../../service/employerapi";
import { useNavigate, useLocation } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import Layout from "./common/App";
import CandidateSearchBar from "../employer/common/CandidateSearchBar";

const CandidateList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const suggestionRef = useRef(null);
  const [showFilter, setShowFilter] = useState(false);

  const queryParams = new URLSearchParams(location.search);
  const initialPage = parseInt(queryParams.get("page")) || 1;
  const initialJobTitle = queryParams.get("jobProfile") || "";
  const initialCity = queryParams.get("city") || "";
  const initialLimit = parseInt(queryParams.get("record_limit")) || 12;
  const initialSkills = queryParams.get("skills") || "";
  const initialEducation = queryParams.get("education") || "";

  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);
  // const [jobTitle, setJobTitle] = useState(initialJobTitle);
  // const [selectedCity, setSelectedCity] = useState(initialCity);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [candidateList, setCandidateList] = useState([]);
  const [totalSeekers, setTotalSeekers] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // INPUT STATES (user typing/selecting)
  const [jobTitleInput, setJobTitleInput] = useState(initialJobTitle);
  const [cityInput, setCityInput] = useState(initialCity);

  // APPLIED STATES (used for API search)
  const [jobTitle, setJobTitle] = useState(initialJobTitle);
  const [selectedCity, setSelectedCity] = useState(initialCity);
  const [skills, setSkills] = useState(initialSkills);
  const [education, setEducation] = useState(initialEducation);

  // ---------------- LOAD SUGGESTIONS ----------------
  const loadSuggestions = async (text) => {
    if (!text) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const res = await getJobTitles(text);
      if (res.status === 200 && res.data?.data) {
        setSuggestions(res.data.data);
        setShowSuggestions(true);
      }
    } catch (e) {
      console.error("Suggestion error:", e);
    }
  };

  // 🔹 Fetch Cities
  useEffect(() => {
    const fetchCities = async () => {
      try {
        setLoadingCities(true);
        const res = await getCities();
        const cityOptions =
          res.data.data?.map((city) => ({
            label: city,
            value: city,
          })) || [];
        setCities(cityOptions);
      } catch (err) {
        console.error("Error fetching cities:", err);
      } finally {
        setLoadingCities(false);
      }
    };
    fetchCities();
  }, []);

  // 🔹 Fetch Candidate List
  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const res = await getCandidateList({
        page,
        limit,
        city: selectedCity,
        jobTitle,
        skills,
        education,
      });
      const data = res.data.data || [];
      setCandidateList(data);
      setTotalSeekers(res.data.totalRecord || 0);
      setTotalPages(res.data.totalPage || 1);
    } catch (error) {
      console.error("Error fetching candidates:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCandidates();
  }, [page, limit, selectedCity, jobTitle, skills, education]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionRef.current &&
        !suggestionRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();

    setJobTitle(jobTitleInput);
    setSelectedCity(cityInput);
    setPage(1);
    setShowSuggestions(false);

    navigate(
      `/employer/candidate-list?page=1&jobProfile=${jobTitleInput}&city=${cityInput}&record_limit=${limit}`,
    );
  };

  // 🔹 Handle Reset
  const handleReset = () => {
    setJobTitleInput("");
    setCityInput("");

    setJobTitle("");
    setSelectedCity("");

    setLimit(12);
    setPage(1);
    setShowSuggestions(false);

    navigate(`/employer/candidate-list`);
  };

  return (
    <Layout>
      {/* ===== Hero Navigation ===== */}
      <section className="innerBnrNav">
        <div className="bnrNav">
          <div className="site-width">
            <div className="bnrNavWrap">
              <ul>
                <li>
                  <a href="/employer-home">Home</a>
                </li>
                <li>
                  <a href="javascript:void(0)">Candidate Listing</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ===== Candidate Section ===== */}
      <section className="featuredjobs pt-50 pb-50">
        <div className="site-width">
          <form onSubmit={handleSearch}>
            {/* Info Row */}
            <div className="listinfviewInfo row align-item-center justify-content-between mt-3">
              <div className="col-md-4">
                <div className="founderinfo">
                  <p>{totalSeekers} Results Found</p>
                </div>
              </div>
              <div className="col-md-4">
                <div className="otherfilter">
                  <select
                    name="record_limit"
                    id="record_limit"
                    value={limit}
                    onChange={(e) => setLimit(parseInt(e.target.value))}
                  >
                    <option value={12}>12 Per Page</option>
                    <option value={24}>24 Per Page</option>
                    <option value={36}>36 Per Page</option>
                  </select>
                </div>
              </div>
            </div>
          </form>

          {/* Mobile Filter Button */}
          <div className="d-block d-md-none mb-3 text-end">
            <button
              className="mainbtn"
              type="button"
              onClick={() => setShowFilter(true)}
            >
              <i className="fa fa-filter"></i> Filter
            </button>
          </div>

          {/* Mobile Filter Offcanvas */}
          <div
            className={`offcanvas offcanvas-start ${showFilter ? "show" : ""}`}
            tabIndex="-1"
            style={{ visibility: showFilter ? "visible" : "hidden" }}
          >
            <div className="offcanvas-header">
              <h5 className="offcanvas-title">Filters</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setShowFilter(false)}
              ></button>
            </div>

            <div className="offcanvas-body">
              <CandidateSearchBar
                initialJobTitle={initialJobTitle}
                initialCity={initialCity}
                initialSkills={initialSkills}
                initialEducation={initialEducation}
                recordLimit={limit}
                customBasePath="/employer/candidate-list"
                onSearch={({ jobTitle, city, skills, education }) => {
                  setJobTitle(jobTitle);
                  setSelectedCity(city);
                  setSkills(skills);
                  setEducation(education);
                  setPage(1);
                }}
              />
            </div>
          </div>

          {/* Offcanvas Backdrop */}
          {showFilter && (
            <div
              className="offcanvas-backdrop fade show"
              onClick={() => setShowFilter(false)}
            ></div>
          )}

          {/* ===== Candidate Cards ===== */}
          <div className="jobListWrapper mt-4">
            <div className="listWrap">
              <div className="row">
                <div className="col-md-4 col-lg-3 d-none d-md-block">
                  {/* <CandidateSearchBar
                    initialJobTitle={initialJobTitle}
                    initialCity={initialCity}
                    recordLimit={limit}
                    customBasePath="/employer/candidate-list"
                    onSearch={({ jobTitle, city }) => {
                      setJobTitle(jobTitle);
                      setSelectedCity(city);
                      setPage(1);
                    }}
                  /> */}
                  <CandidateSearchBar
                    initialJobTitle={initialJobTitle}
                    initialCity={initialCity}
                    initialSkills={initialSkills}
                    initialEducation={initialEducation}
                    recordLimit={limit}
                    customBasePath="/employer/candidate-list"
                    onSearch={({ jobTitle, city, skills, education }) => {
                      setJobTitle(jobTitle);
                      setSelectedCity(city);
                      setSkills(skills);
                      setEducation(education);
                      setPage(1);
                    }}
                  />
                </div>
                <div className="col-md-8 col-lg-9">
                  <div className="row">
                    {loading ? (
                      <div className="text-center py-5">Loading...</div>
                    ) : candidateList.length > 0 ? (
                      candidateList.map((candidate) => (
                        <div
                          key={candidate._id}
                          className="col-md-4 wow animate__zoomIn"
                        >
                          <div className="astrocard candidateCard">
                            <div className="astrocContainer">
                              <div className="astrohead">
                                <div className="astroImg">
                                  <a
                                    href={`/employer/candidate/${candidate._id}`}
                                  >
                                    <img
                                      src={
                                        candidate.image ||
                                        "/assets/img/candidate-profiles/img1.jpg"
                                      }
                                      alt="img"
                                    />
                                  </a>
                                </div>
                                {/* <span className="onlineShow"></span> */}
                              </div>
                              <div className="astroDetails">
                                <div className="astroName">
                                  <a
                                    href={`/employer/candidate/${candidate._id}`}
                                  >
                                    <h4>
                                      {candidate.name?.length > 20
                                        ? candidate.name.slice(0, 20) + "..."
                                        : candidate.name}{" "}
                                      {/* <img
                                  src="/assets/img/icons/check.svg"
                                  className="verifiedCheck"
                                  alt="icon"
                                /> */}
                                    </h4>
                                  </a>
                                  <ul className="astroAcheiver companyName">
                                    <li>
                                      <img
                                        src="/assets/img/icons/user-line-color.svg"
                                        width="20"
                                        alt="icon"
                                      />
                                      <p>{candidate.jobProfile}</p>
                                    </li>{" "}
                                  </ul>
                                  <div className="astroinfo">
                                    <li>
                                      <img
                                        src="/assets/img/icons/location-.svg"
                                        alt="icon"
                                      />
                                      <span>{candidate.city}</span>
                                    </li>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {candidate.skills &&
                              candidate.skills.length > 0 && (
                                <div className="astrolBtns flex-wrap">
                                  {candidate.skills
                                    .slice(0, 2)
                                    .map((skill, index) => (
                                      <a
                                        href="javascript:void(0)"
                                        key={index}
                                        className="astrocatbadge"
                                        title={skill} // show full skill on hover
                                      >
                                        {skill.length > 10
                                          ? skill.slice(0, 10) + "…"
                                          : skill}
                                      </a>
                                    ))}

                                  {candidate.skills.length > 2 && (
                                    <span className="astrocatbadge">
                                      +{candidate.skills.length - 2} more
                                    </span>
                                  )}
                                </div>
                              )}

                            <div className="d-flex gap-2 align-item-center w-100 joblistfooter">
                              <span
                                style={{ height: "fit-content" }}
                                className="joblistrate"
                              >
                                <img
                                  src="/assets/img/icons/time.svg"
                                  alt="img"
                                  style={{ width: "20px" }}
                                />{" "}
                                ₹{candidate.CurrentSalary}/Month
                              </span>
                              <a
                                href={`/employer/candidate/${candidate._id}`}
                                className="mainbtn"
                              >
                                View Details
                              </a>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-5">
                        No candidates found.
                      </div>
                    )}
                  </div>

                  {/* ===== Pagination ===== */}
                  <nav aria-label="Page navigation example">
                    <ul className="pagination justify-content-center mt-4">
                      <li
                        className={`page-item ${page === 1 ? "disabled" : ""}`}
                      >
                        <a
                          className="page-link"
                          href="#"
                          onClick={() => page > 1 && setPage(page - 1)}
                        >
                          Previous
                        </a>
                      </li>

                      {[...Array(totalPages)].map((_, i) => (
                        <li
                          key={i}
                          className={`page-item ${i + 1 === page ? "active" : ""}`}
                        >
                          <a
                            className="page-link"
                            href="#"
                            onClick={() => setPage(i + 1)}
                          >
                            {i + 1}
                          </a>
                        </li>
                      ))}

                      <li
                        className={`page-item ${page === totalPages ? "disabled" : ""}`}
                      >
                        <a
                          className="page-link"
                          href="#"
                          onClick={() => page < totalPages && setPage(page + 1)}
                        >
                          Next
                        </a>
                      </li>
                    </ul>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== App Banner ===== */}
      <section className="appBanner pt-40 pb-50">
        <div className="site-width">
          <div className="appbannerwrapper">
            <div className="row align-item-center">
              <div className="col-md-7">
                <div className="apbnrtitle">
                  <h3>100K+ users are on the Hourlee App</h3>
                  <p>
                    Join thousands of employers and job seekers who use Hourlee
                    to connect in real-time. Whether you’re hiring for a few
                    hours or looking for quick work, the app makes it simple,
                    fast, and flexible — right from your phone.
                  </p>
                  <h5>
                    Get started in seconds — just enter your number and we’ll
                    text you the app link.
                  </h5>
                </div>
                <div className="appbnrinput">
                  <input type="text" placeholder="Enter mobile number..." />
                  <button>Get Link</button>
                </div>
                <div className="aapbnrdownloadbtns">
                  <a href="javascript:void(0)">
                    <img
                      src="/assets/img/other/GplayOneThemeHd.png"
                      alt="img"
                    />
                  </a>
                  <a href="javascript:void(0)">
                    <img
                      src="/assets/img/other/AppstoreOneThemeHd.png"
                      alt="img"
                    />
                  </a>
                </div>
              </div>
              <div className="col-md-4 offset-1">
                <div className="appbnrimg">
                  <img src="/assets/img/other/aapimg-employer.png" alt="img" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CandidateList;
